/**
 * Illustrative utilization data, derived from the June 2026 timesheet-report
 * aggregates (38,538 rows / 261 workers) that the source workbook pivots.
 *
 * **This module is the shape the UI expects, not a data source.** When the
 * dashboard APIs land, each export here becomes a TanStack Query hook and the
 * file is deleted; nothing outside `utilization/` imports it, so components
 * never need to change with it.
 *
 * Deliberately NO targets or thresholds: the dashboard shows the data and lets
 * the reader judge what is good or bad.
 */

import { SERIES } from '../charts/chart-theme';

export type CategoryKey = 'hourly' | 'retainer' | 'free' | 'internal' | 'leave' | 'idle';

export interface Category {
  key: CategoryKey;
  label: string;
  color: string;
}

/** Utilization categories in stack order — client work split by engagement type. */
export const CATEGORIES: Category[] = [
  { key: 'hourly', label: 'Hourly', color: SERIES.hourly },
  { key: 'retainer', label: 'Retainer', color: SERIES.retainer },
  { key: 'free', label: '$1+Free / Dead / MTF', color: SERIES.free },
  { key: 'internal', label: 'Internal (msCFO)', color: SERIES.internal },
  { key: 'leave', label: 'Leave / Holiday', color: SERIES.leave },
  { key: 'idle', label: 'Idle / Balance', color: SERIES.idle },
];

export interface Group {
  name: string;
  status: string;
  /** Net-available hours: expected hours − leave − holiday. */
  available: number;
  /** Client work by engagement type. `hourly + retainer === billable + nonBillable`. */
  hourly: number;
  retainer: number;
  /** The same client work by billability — both margins are reported. */
  billable: number;
  nonBillable: number;
  free: number;
  internal: number;
  leave: number;
  idle: number;
  people: number;
}

/** Hours actually booked: client work of every kind plus internal. Excludes leave and idle. */
export function committedHours(g: Group): number {
  return g.hourly + g.retainer + g.free + g.internal;
}

export interface EngagementCell {
  billable: number;
  nonBillable: number;
  total: number;
}

/**
 * Engagement × billability cross-tab for a group.
 *
 * A group stores only the two *margins* (hourly/retainer and billable/
 * non-billable), so the 2×2 interior has to be solved: take hourly-billable
 * near the firm-wide rate, then clamp so both margins reconcile exactly and
 * no cell can go negative.
 *
 * **Prototype bridge — delete when real data lands.** The timesheet report
 * carries `customer_type` and `billable` on every entry, so production gets
 * the true cross-tab from a group-by and never has to infer it.
 */
export function engagementSplit(g: Group): Record<EngagementKey, EngagementCell> {
  const floor = Math.max(0, g.billable - g.retainer); // retainer non-billable can't go negative
  const ceiling = Math.min(g.hourly, g.billable); // can't exceed either margin
  // When the two margins don't reconcile (billable > hourly + retainer) the
  // window is inverted and `floor > ceiling`. Clamping the floor up to the
  // ceiling keeps the solution inside the feasible box; the residual
  // inconsistency then shows as a smaller billable total rather than as a
  // negative cell, which would render as an inverted bar.
  const target = Math.round(g.hourly * 0.78);
  const hourlyBillable = Math.max(0, Math.min(ceiling, Math.max(Math.min(floor, ceiling), target)));
  const retainerBillable = Math.max(0, Math.min(g.retainer, g.billable - hourlyBillable));
  return {
    hourly: {
      billable: hourlyBillable,
      nonBillable: Math.max(0, g.hourly - hourlyBillable),
      total: g.hourly,
    },
    retainer: {
      billable: retainerBillable,
      nonBillable: Math.max(0, g.retainer - retainerBillable),
      total: g.retainer,
    },
  };
}

/** Summary rows grouped by location & status. */
export const LOCATIONS: Group[] = [
  {
    name: 'Bangalore',
    status: 'India · ACT',
    available: 20034,
    hourly: 2287,
    retainer: 11163,
    billable: 10850,
    nonBillable: 2600,
    free: 520,
    internal: 4180,
    leave: 1400,
    idle: 900,
    people: 118,
  },
  {
    name: 'Udupi',
    status: 'India · ACT',
    available: 3696,
    hourly: 339,
    retainer: 1921,
    billable: 1740,
    nonBillable: 520,
    free: 90,
    internal: 640,
    leave: 360,
    idle: 520,
    people: 31,
  },
  {
    name: 'USA',
    status: 'On-shore',
    available: 3480,
    hourly: 846,
    retainer: 2174,
    billable: 2610,
    nonBillable: 410,
    free: 70,
    internal: 520,
    leave: 210,
    idle: -70,
    people: 19,
  },
  {
    name: 'Delhi',
    status: 'India · ACT',
    available: 2079,
    hourly: 204,
    retainer: 1256,
    billable: 1180,
    nonBillable: 280,
    free: 60,
    internal: 340,
    leave: 150,
    idle: 130,
    people: 16,
  },
  {
    name: 'Chandigarh',
    status: 'India · ACT',
    available: 1856,
    hourly: 133,
    retainer: 977,
    billable: 920,
    nonBillable: 190,
    free: 40,
    internal: 300,
    leave: 210,
    idle: 296,
    people: 12,
  },
  {
    name: 'Delhi-MDG',
    status: 'MDG',
    available: 176,
    hourly: 11,
    retainer: 99,
    billable: 96,
    nonBillable: 14,
    free: 4,
    internal: 36,
    leave: 12,
    idle: 14,
    people: 2,
  },
];

/** Business-segment roll-up (the workbook's grand-total rows). */
export const SEGMENTS: Group[] = [
  {
    name: 'ACT + MDG',
    status: 'Client delivery',
    available: 31321,
    hourly: 3504,
    retainer: 18396,
    billable: 17800,
    nonBillable: 4100,
    free: 780,
    internal: 6200,
    leave: 1900,
    idle: 541,
    people: 190,
  },
  {
    name: 'CaaS',
    status: 'Controller-as-a-Service',
    available: 2618,
    hourly: 372,
    retainer: 1488,
    billable: 1500,
    nonBillable: 360,
    free: 60,
    internal: 420,
    leave: 160,
    idle: 118,
    people: 14,
  },
  {
    name: 'Ops',
    status: 'BEF / back-office',
    available: 3160,
    hourly: 55,
    retainer: 495,
    billable: 300,
    nonBillable: 250,
    free: 40,
    internal: 1900,
    leave: 300,
    idle: 370,
    people: 30,
  },
  {
    name: 'GL',
    status: 'General ledger',
    available: 880,
    hourly: 21,
    retainer: 189,
    billable: 120,
    nonBillable: 90,
    free: 20,
    internal: 500,
    leave: 80,
    idle: 70,
    people: 8,
  },
  {
    name: 'Exiting',
    status: 'Off-boarding',
    available: 1504,
    hourly: 83,
    retainer: 467,
    billable: 400,
    nonBillable: 150,
    free: 30,
    internal: 300,
    leave: 250,
    idle: 374,
    people: 19,
  },
];

export const FIRM = {
  available: 39482,
  committed: 38395,
  billable: 21437,
  nonBillable: 5028,
  free: 937,
  internal: 10993,
  leave: 2591,
  idle: 1087,
  people: 261,
};

export interface Kpi {
  label: string;
  value: string;
  delta: string;
  /** Whether the metric moved up. Direction only — no judgement about good/bad. */
  up: boolean;
  trend: number[];
}

export const KPIS: Kpi[] = [
  {
    label: 'Overall Utilization',
    value: '96.6%',
    delta: '+1.4',
    up: true,
    trend: [89, 91, 90, 93, 92, 94, 93, 95, 94, 95, 95, 96.6],
  },
  {
    label: 'Client Billable %',
    value: '54.3%',
    delta: '-0.8',
    up: false,
    trend: [57, 58, 56, 57, 55, 56, 55, 54, 55, 54, 55, 54.3],
  },
  {
    label: 'Client Non-billable %',
    value: '12.7%',
    delta: '+0.4',
    up: true,
    trend: [11.4, 11.6, 11.9, 12, 12.2, 12.1, 12.4, 12.3, 12.5, 12.4, 12.3, 12.7],
  },
  {
    label: 'Internal (msCFO) %',
    value: '27.8%',
    delta: '+1.1',
    up: true,
    trend: [24, 25, 25, 26, 26, 27, 27, 27, 28, 27, 27, 27.8],
  },
  {
    label: 'Available Hours',
    value: '39.5k',
    delta: '+0.6k',
    up: true,
    trend: [36, 37, 37, 38, 38, 38, 39, 38, 39, 39, 39, 39.5],
  },
  {
    label: 'Committed Hours',
    value: '38.4k',
    delta: '+0.9k',
    up: true,
    trend: [34, 35, 35, 36, 37, 37, 38, 37, 38, 38, 38, 38.4],
  },
];

export type EngagementKey = 'hourly' | 'retainer';

export interface Engagement {
  key: EngagementKey;
  name: string;
  billable: number;
  nonBillable: number;
  note: string;
  color: string;
  /** 12-month total hours. */
  trend: number[];
  /** 12-month billable hours; non-billable is the difference. */
  trendBillable: number[];
}

export const ENGAGEMENTS: Engagement[] = [
  {
    key: 'hourly',
    name: 'Hourly',
    billable: 3500,
    nonBillable: 991,
    note: 'T&M / hourly',
    color: SERIES.hourly,
    trend: [3820, 3960, 4110, 4180, 4020, 4300, 4390, 4240, 4520, 4610, 4400, 4491],
    trendBillable: [2980, 3090, 3210, 3260, 3130, 3360, 3420, 3300, 3530, 3600, 3430, 3500],
  },
  {
    key: 'retainer',
    name: 'Retainer',
    billable: 17295,
    nonBillable: 4443,
    note: 'Monthly retainer',
    color: SERIES.retainer,
    trend: [19600, 20050, 20380, 20820, 21010, 21240, 21520, 21290, 21810, 22030, 21580, 21738],
    trendBillable: [
      15600, 15980, 16250, 16590, 16720, 16900, 17140, 16950, 17370, 17540, 17180, 17295,
    ],
  },
];

/** Trailing 12 months, oldest first. */
export const MONTHS = [
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
];

/* ------------------------------- people -------------------------------- */

/** Service-role ladder, lowest → highest. Ordering is what makes mismatch directional. */
export const ROLES = ['BK', 'ACCT', 'CTR', 'CFO'] as const;
export type Role = (typeof ROLES)[number];

export function roleLevel(role: Role): number {
  return ROLES.indexOf(role);
}

/** <0 = booked below their level, 0 = at level, >0 = booked above their level. */
export function levelDelta(p: Person): number {
  return roleLevel(p.workedRole) - roleLevel(p.designatedRole);
}

/** Tenure cohort boundary, in months. */
export const TENURE_SPLIT = 6;

export type PayType = 'FTE' | 'Consultant';

export interface Person {
  name: string;
  /** The level the person is employed at (`users.service_role_id`). */
  designatedRole: Role;
  /** The level most of their hours were actually booked at (entry service role). */
  workedRole: Role;
  payType: PayType;
  manager: string;
  pod: 'POD' | 'Non-POD';
  /** Months since start date. */
  tenure: number;
  /** Business segment the person delivers into. */
  segment: string;
  available: number;
  committed: number;
  billable: number;
  nonBillable: number;
  internal: number;
  /** Hours booked at a level other than their designated role. */
  mismatchHours: number;
  utilization: number;
  location?: string;
}

const NAMES = [
  'Adithya Venkataramana',
  'Amelita Martis',
  'Sunil Thekkummuri',
  'Mohith Raj',
  'Priyanka Gupta',
  'Inderjit Singh',
  'Vinithra Velusamy',
  'Rina Agadi',
  'Pritika Sood',
  'Love Kumar',
  'Meghana Murthy',
  'Dev Sharma',
  'Anita Rao',
  'Karan Mehta',
  'Sofia Diaz',
  'Wei Chen',
];
const MANAGERS = [
  'Vivian Feng',
  'Dolly Gupta',
  'Amanda Tabb',
  'Sneha Ramachandra',
  'Jaspreet Kaur',
  'Manju Bala',
];
/** Deterministic level shifts: mostly at-level, some below (over-qualified), a few above (stretch). */
const SHIFTS = [0, 0, -1, 0, 1, 0, -1, 0, -2, 0, 1, 0];
const AVAILABLE_BY_INDEX = [160, 176, 176, 168, 176];
/** Segment names, for assigning fixture people to a delivery segment. */
/**
 * Segment names weighted by their declared headcount, so a person picked off
 * this table lands in a segment in roughly the proportion the Segment mix tile
 * reports. Cycling the bare name list instead spread people evenly and made
 * the Role Mismatch tile (grouped by segment) contradict the mix tile's own
 * "N ppl" captions on the same screen.
 */
const SEGMENT_SLOTS: string[] = SEGMENTS.flatMap((seg) =>
  Array.from({ length: Math.max(1, Math.round((seg.people / FIRM.people) * 20)) }, () => seg.name),
);
const TENURE_BY_INDEX = [3, 8, 14, 2, 40, 60, 4, 22];

/**
 * Wrap-around index into a non-empty constant table.
 *
 * These fixtures cycle through fixed lists to stay deterministic, so the index
 * is always in range — but `noUncheckedIndexedAccess` can't know that. The
 * assertion is contained here instead of being repeated at a dozen call sites.
 */
function cycle<T>(items: readonly T[], index: number): T {
  return items[((index % items.length) + items.length) % items.length] as T;
}

/**
 * Deterministic synthetic people for a group's drilldown. Seeded off the group
 * name so the same group always yields the same roster — a drilldown that
 * reshuffled on every open would be unusable.
 */
export function peopleFor(g: Group, count: number): Person[] {
  const rows: Person[] = [];
  const seed = g.name.length;
  for (let i = 0; i < count; i++) {
    const utilization = Math.max(
      38,
      Math.min(112, Math.round(72 + ((i * 37 + seed * 11) % 40) - (i % 3) * 7)),
    );
    const available = cycle(AVAILABLE_BY_INDEX, i);
    const committed = Math.round((available * utilization) / 100);
    const billable = Math.round(committed * 0.58);
    const internal = Math.round(committed * 0.22);
    const nonBillable = Math.max(0, committed - billable - internal);
    const designatedRole = cycle(ROLES, i + seed);
    const shift = cycle(SHIFTS, i * 3 + seed);
    const workedRole = cycle(
      ROLES,
      Math.max(0, Math.min(ROLES.length - 1, roleLevel(designatedRole) + shift)),
    );
    const mismatchHours =
      workedRole === designatedRole ? 0 : Math.round(committed * (0.22 + (i % 4) * 0.11));
    rows.push({
      name: cycle(NAMES, i + seed),
      designatedRole,
      workedRole,
      payType: i % 4 === 0 ? 'Consultant' : 'FTE',
      manager: cycle(MANAGERS, i + seed),
      pod: i % 3 === 0 ? 'POD' : 'Non-POD',
      tenure: cycle(TENURE_BY_INDEX, i),
      segment: cycle(SEGMENT_SLOTS, i + seed),
      available,
      committed,
      billable,
      nonBillable,
      internal,
      mismatchHours,
      utilization,
    });
  }
  return rows.sort((a, b) => a.utilization - b.utilization);
}

/** Firm-wide population, used by the role-mismatch tile and every drilldown. */
export const ALL_PEOPLE: Person[] = LOCATIONS.flatMap((l) =>
  peopleFor(l, Math.min(l.people, 10)).map((p) => ({ ...p, location: l.name })),
);

export function belowLevelHours(people: Person[]): number {
  return people.filter((p) => levelDelta(p) < 0).reduce((sum, p) => sum + p.mismatchHours, 0);
}

export function aboveLevelHours(people: Person[]): number {
  return people.filter((p) => levelDelta(p) > 0).reduce((sum, p) => sum + p.mismatchHours, 0);
}

/**
 * 12-month history of off-level hours, ramped to land exactly on the current
 * month's real figure. Synthesized because only one month of actuals exists.
 */
export const MISMATCH_TREND = (() => {
  const below = belowLevelHours(ALL_PEOPLE);
  const above = aboveLevelHours(ALL_PEOPLE);
  return MONTHS.map((month, i) => {
    if (i === MONTHS.length - 1) return { month, below, above };
    const ramp = 0.68 + i * 0.028;
    return {
      month,
      below: Math.round(below * ramp * (1 + Math.sin(i * 0.9) * 0.06)),
      above: Math.round(above * ramp * (1 + Math.cos(i * 0.7) * 0.08)),
    };
  });
})();
